import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../services/supabaseService';
import { Meeting } from '../types';
import { ToastMessage } from '../components/RealtimeToast';

interface RealtimePayload {
  eventType: 'INSERT' | 'UPDATE' | 'DELETE';
  new: any;
  old: any;
}

interface UseRealtimeMeetingsOptions {
  onToast?: (message: ToastMessage) => void;
  enabled?: boolean;
}

export const useRealtimeMeetings = (options: UseRealtimeMeetingsOptions = {}) => {
  const { onToast, enabled = true } = options;
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled) {
      console.log('🔌 Realtime deshabilitado');
      return;
    }

    console.log('🔌 Configurando suscripción Realtime para meetings...');

    const channel = supabase
      .channel('meetings-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'meetings'
        },
        async (payload: RealtimePayload) => {
          console.log('📡 Evento Realtime recibido:', payload.eventType, payload);

          const meeting = payload.new || payload.old;

          let userName = 'Un usuario';
          if (meeting?.modified_by || meeting?.created_by) {
            try {
              const userId = meeting.modified_by || meeting.created_by;
              const { usersService } = await import('../services/supabaseService');
              const user = await usersService.getById(userId);
              userName = user?.name || userName;
            } catch (error) {
              console.warn('⚠️ No se pudo obtener nombre de usuario:', error);
            }
          }

          if (onToast) {
            // Check if this meeting is from Google Calendar
            const isFromGoogle = meeting?.google_event_id || meeting?.sync_source === 'google';

            const toastMessage: ToastMessage = {
              id: `toast-${Date.now()}-${Math.random()}`,
              type: payload.eventType.toLowerCase() as 'create' | 'update' | 'delete',
              title: '',
              message: '',
              userName: isFromGoogle ? 'Google Calendar' : userName,
              meetingId: meeting?.id,
              timestamp: Date.now()
            };

            switch (payload.eventType) {
              case 'INSERT':
                if (isFromGoogle) {
                  toastMessage.title = '📅 Nueva reunión de Google Calendar';
                  toastMessage.message = `Se ha importado "${meeting.title}" desde tu calendario`;
                  // Show participants if available
                  if (meeting.participants && meeting.participants.length > 0) {
                    toastMessage.message += ` con ${meeting.participants.slice(0, 2).join(', ')}`;
                    if (meeting.participants.length > 2) {
                      toastMessage.message += ` y ${meeting.participants.length - 2} más`;
                    }
                  }
                } else {
                  toastMessage.title = 'Nueva reunión creada';
                  toastMessage.message = `Se ha creado la reunión "${meeting.title}"`;
                }
                break;
              case 'UPDATE':
                if (isFromGoogle) {
                  toastMessage.title = '🔄 Reunión de Google actualizada';
                  toastMessage.message = `Se ha sincronizado "${meeting.title}"`;
                } else {
                  toastMessage.title = 'Reunión actualizada';
                  toastMessage.message = `Se ha actualizado la reunión "${meeting.title}"`;
                }
                break;
              case 'DELETE':
                toastMessage.title = 'Reunión eliminada';
                toastMessage.message = `Se ha eliminado una reunión`;
                break;
            }

            onToast(toastMessage);
          }

          window.dispatchEvent(
            new CustomEvent('realtime-meeting-change', {
              detail: {
                eventType: payload.eventType,
                meeting: payload.new || payload.old
              }
            })
          );
        }
      )
      .subscribe((status) => {
        console.log('📡 Estado de suscripción Realtime:', status);
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      console.log('🔌 Desconectando suscripción Realtime...');
      supabase.removeChannel(channel);
      setIsConnected(false);
    };
  }, [enabled, onToast]);

  return { isConnected };
};

export const useRealtimeMeetingListener = (
  callback: (event: { eventType: 'INSERT' | 'UPDATE' | 'DELETE'; meeting: any }) => void,
  enabled: boolean = true
) => {
  useEffect(() => {
    if (!enabled) return;

    const handleRealtimeChange = (event: CustomEvent) => {
      callback(event.detail);
    };

    window.addEventListener('realtime-meeting-change', handleRealtimeChange as EventListener);

    return () => {
      window.removeEventListener('realtime-meeting-change', handleRealtimeChange as EventListener);
    };
  }, [callback, enabled]);
};
